import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import 'photoswipe/style.css';
import '../styles/photoswipe-custom.css';

// GraphQL Query
const GET_GALLERY_DETAIL = gql`
  query GetGalleryDetail($slug: String!) {
    gallery(slug: $slug) {
      _id
      name
      description
      slug
      coverImage
      category
      tags
      status
      visibility
      imageCount
      metadata {
        views
        likes {
          user {
            id
          }
        }
      }
      settings {
        allowComments
        allowDownload
        layout
      }
      event {
        name
        date
        location
      }
      images {
        _id
        url
        thumbnailUrl
        alt
        caption
        width
        height
        metadata {
          camera
          lens
          settings {
            iso
            aperture
            shutterSpeed
            focalLength
          }
          location {
            name
          }
          tags
        }
      }
      author {
        username
        profile {
          firstName
          lastName
        }
      }
      createdAt
    }
  }
`;

interface GalleryImage {
  _id: string;
  url: string;
  thumbnailUrl?: string;
  alt?: string;
  caption?: string;
  width: number;
  height: number;
  metadata?: {
    camera?: string;
    lens?: string;
    settings?: {
      iso?: number;
      aperture?: string;
      shutterSpeed?: string;
      focalLength?: string;
    };
    location?: {
      name?: string;
    };
    tags?: string[];
  };
}

interface Gallery {
  _id: string;
  name: string;
  description?: string;
  slug: string;
  coverImage?: string;
  category: string;
  tags: string[];
  status: string;
  visibility: string;
  imageCount: number;
  metadata: {
    views: number;
    likes: Array<{ user: { id: string } }>;
  };
  settings: {
    allowComments: boolean;
    allowDownload: boolean;
    layout: string;
  };
  event?: {
    name?: string;
    date?: string;
    location?: string;
  };
  images: GalleryImage[];
  author: {
    username: string;
    profile: {
      firstName?: string;
      lastName?: string;
    };
  };
  createdAt: string;
}

const GalleryDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  // Stati per FolderGallery (fallback)
  const [folderGallery, setFolderGallery] = useState<any>(null);
  const [folderLoading, setFolderLoading] = useState(false);
  const [folderError, setFolderError] = useState<string | null>(null);

  const { data, loading, error } = useQuery(GET_GALLERY_DETAIL, {
    variables: { slug },
    skip: !slug
  });

  const gallery: Gallery | undefined = data?.gallery;

  // Usa le variabili d'ambiente
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
  const BACKEND_URL = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api').replace('/api', '');

  // Se GraphQL fallisce, prova a caricare da FolderGallery API
  useEffect(() => {
    if (error && slug && !folderGallery && !folderLoading) {
      const loadFolderGallery = async () => {
        try {
          setFolderLoading(true);
          setFolderError(null);

          const response = await fetch(`${API_BASE_URL}/gallery/folders/${slug}`);
          const result = await response.json();

          if (result.success && result.gallery) {
            // Converti FolderGallery nel formato Gallery
            const convertedGallery = {
              _id: result.gallery.id,
              name: result.gallery.name,
              description: result.gallery.description || '',
              slug: result.gallery.slug,
              coverImage: result.gallery.coverImage,
              category: result.gallery.category,
              tags: [],
              status: 'published',
              visibility: 'public',
              imageCount: result.gallery.imageCount,
              metadata: {
                views: 0,
                likes: []
              },
              settings: {
                allowComments: false,
                allowDownload: false,
                layout: 'grid'
              },
              event: {
                name: result.gallery.name,
                date: result.gallery.date,
                location: ''
              },
              images: result.gallery.images.map((img: any) => ({
                _id: img.id,
                url: `${BACKEND_URL}${img.url}`,
                thumbnailUrl: `${BACKEND_URL}${img.thumbnailUrl || img.url}`,
                alt: img.alt || img.title,
                caption: img.title,
                width: 0, // Sarà calcolato automaticamente da PhotoSwipe
                height: 0, // Sarà calcolato automaticamente da PhotoSwipe
                metadata: {}
              })),
              author: {
                username: 'UNASCA',
                profile: {
                  firstName: 'Redazione',
                  lastName: 'UNASCA'
                }
              },
              createdAt: result.gallery.date || new Date().toISOString()
            };

            setFolderGallery(convertedGallery);
          } else {
            setFolderError('Galleria non trovata');
          }
        } catch (err) {
          console.error('Errore nel caricamento della FolderGallery:', err);
          setFolderError('Errore nel caricamento della galleria');
        } finally {
          setFolderLoading(false);
        }
      };

      loadFolderGallery();
    }
  }, [error, slug, folderGallery, folderLoading, API_BASE_URL, BACKEND_URL]);

  // Usa folderGallery se gallery non è disponibile
  const displayGallery = gallery || folderGallery;

  // Inizializza PhotoSwipe
  useEffect(() => {
    if (!displayGallery?.images || displayGallery.images.length === 0) return;

    const lightbox = new PhotoSwipeLightbox({
      gallery: '#gallery-detail-container',
      children: 'a',
      pswpModule: () => import('photoswipe'),

      // Opzioni visualizzazione
      bgOpacity: 0.95,
      spacing: 0.1,
      allowPanToNext: true,
      loop: true,

      // Zoom avanzato
      zoom: true,
      maxZoomLevel: 4,
      initialZoomLevel: 'fit', // 'fit' adatta l'immagine allo schermo preservando proporzioni
      secondaryZoomLevel: 1.5,

      // Controlli
      pinchToClose: true,
      closeOnVerticalDrag: true,
      escKey: true,
      arrowKeys: true,
      returnFocus: true,
      clickToCloseNonZoomable: true,
      imageClickAction: 'zoom',
      tapAction: 'toggle-controls',
      doubleTapAction: 'zoom',

      // Animazioni
      showHideAnimationType: 'fade',
      showAnimationDuration: 333,
      hideAnimationDuration: 333,

      // Preload
      preload: [1, 3],
      preloaderDelay: 0,

      // UI Elements
      closeTitle: 'Chiudi (Esc)',
      zoomTitle: 'Zoom',
      arrowPrevTitle: 'Precedente',
      arrowNextTitle: 'Successiva',
      errorMsg: 'Impossibile caricare l\'immagine',
    });

    // Variabile per gestire lo slideshow
    let slideshowInterval: NodeJS.Timeout | null = null;
    let isSlideshow = false;

    // Aggiungi pulsanti personalizzati
    lightbox.on('uiRegister', function() {
      // Pulsante Slideshow
      lightbox.pswp?.ui?.registerElement({
        name: 'slideshow-button',
        order: 7,
        isButton: true,
        tagName: 'button',
        html: {
          isCustomSVG: true,
          inner: '<path d="M8 5v14l11-7z" id="pswp__icn-play"/>',
          outlineID: 'pswp__icn-play'
        },
        onClick: (event: any, el: any, pswp: any) => {
          if (!isSlideshow) {
            // Avvia slideshow
            isSlideshow = true;
            el.innerHTML = '<svg class="pswp__icn" viewBox="0 0 24 24"><path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" id="pswp__icn-pause"/></svg>';
            el.setAttribute('title', 'Pausa slideshow');

            slideshowInterval = setInterval(() => {
              if (pswp) {
                const currentIndex = pswp.currIndex;
                const totalSlides = pswp.getNumItems();

                if (currentIndex < totalSlides - 1) {
                  pswp.next();
                } else {
                  pswp.goTo(0); // Torna alla prima
                }
              }
            }, 3000); // 3 secondi
          } else {
            // Ferma slideshow
            isSlideshow = false;
            if (slideshowInterval) {
              clearInterval(slideshowInterval);
              slideshowInterval = null;
            }
            el.innerHTML = '<svg class="pswp__icn" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" id="pswp__icn-play"/></svg>';
            el.setAttribute('title', 'Avvia slideshow (3 sec)');
          }
        },
        onInit: (el: any) => {
          el.setAttribute('title', 'Avvia slideshow (3 sec)');
        }
      });

      // Pulsante Download
      lightbox.pswp?.ui?.registerElement({
        name: 'download-button',
        order: 8,
        isButton: true,
        tagName: 'a',
        html: {
          isCustomSVG: true,
          inner: '<path d="M20.5 14.3 17.1 18V10h-2.2v7.9l-3.4-3.6L10 16l6 6.1 6-6.1ZM23 23H9v2h14Z" id="pswp__icn-download"/>',
          outlineID: 'pswp__icn-download'
        },
        onInit: (el: any, pswp: any) => {
          el.setAttribute('download', '');
          el.setAttribute('target', '_blank');
          el.setAttribute('rel', 'noopener');
          el.setAttribute('title', 'Scarica immagine');

          pswp.on('change', () => {
            const currentSlide = pswp.currSlide;
            if (currentSlide?.data?.src) {
              el.href = currentSlide.data.src;
            }
          });
        }
      });

      // Pulsante Condividi
      lightbox.pswp?.ui?.registerElement({
        name: 'share-button',
        order: 9,
        isButton: true,
        tagName: 'button',
        html: {
          isCustomSVG: true,
          inner: '<path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z" id="pswp__icn-share"/>',
          outlineID: 'pswp__icn-share'
        },
        onClick: (event: any, el: any, pswp: any) => {
          const currentSlide = pswp.currSlide;
          if (currentSlide?.data?.src) {
            const shareData = {
              title: displayGallery.name,
              text: `Guarda questa foto dalla galleria "${displayGallery.name}"`,
              url: window.location.href
            };

            if (navigator.share) {
              navigator.share(shareData).catch(() => {
                copyToClipboard(window.location.href);
              });
            } else {
              copyToClipboard(window.location.href);
            }
          }
        }
      });

      // Pulsante Info
      lightbox.pswp?.ui?.registerElement({
        name: 'info-button',
        order: 10,
        isButton: true,
        tagName: 'button',
        html: {
          isCustomSVG: true,
          inner: '<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" id="pswp__icn-info"/>',
          outlineID: 'pswp__icn-info'
        },
        onClick: (event: any, el: any, pswp: any) => {
          const currentSlide = pswp.currSlide;
          const caption = currentSlide?.data?.element?.querySelector('img')?.alt || 'Nessuna descrizione';
          alert(`📷 ${caption}\n\n🔍 Usa la rotella del mouse o pinch per zoomare\n⌨️ Usa le frecce per navigare\n💾 Clicca il pulsante download per salvare`);
        }
      });
    });

    // Helper per copiare negli appunti
    const copyToClipboard = (text: string) => {
      navigator.clipboard.writeText(text).then(() => {
        alert('✅ Link copiato negli appunti!');
      }).catch(() => {
        alert('❌ Impossibile copiare il link');
      });
    };

    // Ferma slideshow quando si chiude la galleria
    lightbox.on('close', () => {
      if (slideshowInterval) {
        clearInterval(slideshowInterval);
        slideshowInterval = null;
        isSlideshow = false;
      }
    });

    lightbox.init();

    return () => {
      // Cleanup: ferma slideshow e distruggi lightbox
      if (slideshowInterval) {
        clearInterval(slideshowInterval);
      }
      lightbox.destroy();
    };
  }, [displayGallery]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getLayoutClass = (layout: string) => {
    switch (layout) {
      case 'masonry':
        return 'columns-1 sm:columns-2 md:columns-3 lg:columns-4 gap-4';
      case 'slideshow':
        return 'grid grid-cols-1';
      default:
        return 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6';
    }
  };

  if (loading || folderLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Caricamento galleria...</p>
        </div>
      </div>
    );
  }

  if ((error && folderError) || !displayGallery) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Galleria non trovata
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            La galleria che stai cercando non esiste o non è più disponibile.
          </p>
          <button
            onClick={() => navigate('/gallery')}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
          >
            Torna alla Galleria
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Header della Galleria: verde scuro con la prima foto dell'album sullo sfondo */}
      <div className="relative isolate overflow-hidden bg-primary-950 shadow-xl">
        {displayGallery.images[0] && (
          <img
            src={displayGallery.images[0].thumbnailUrl || displayGallery.images[0].url}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 -z-10 h-full w-full object-cover opacity-35"
          />
        )}
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-primary-950 via-primary-950/85 to-primary-950/40" />
        <div className="container-custom py-10 md:py-14">
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => navigate('/gallery')}
              className="flex items-center text-white hover:text-green-100 font-medium transition-all duration-200 hover:translate-x-[-4px]"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Torna alla Galleria
            </button>

            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <span className="text-sm font-medium text-white">
                  {displayGallery.metadata.views}
                </span>
              </div>
              <div className="flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                </svg>
                <span className="text-sm font-medium text-white">
                  {displayGallery.metadata.likes.length}
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-white mb-4 drop-shadow-lg">
                {displayGallery.name}
              </h1>

              {displayGallery.description && (
                <p className="text-green-50 text-lg mb-6 leading-relaxed">
                  {displayGallery.description}
                </p>
              )}

              {displayGallery.event && (
                <div className="bg-white/10 backdrop-blur-md rounded-xl p-5 mb-6 border border-white/20 shadow-lg">
                  <h3 className="font-semibold text-white mb-3 flex items-center text-lg">
                    <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {displayGallery.event.name}
                  </h3>
                  {displayGallery.event.date && (
                    <p className="text-green-50 text-sm mb-2 flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {formatDate(displayGallery.event.date)}
                    </p>
                  )}
                  {displayGallery.event.location && (
                    <p className="text-green-50 text-sm flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {displayGallery.event.location}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="lg:col-span-1">
              <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20 shadow-lg">
                <h3 className="font-semibold text-white mb-5 text-lg flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Dettagli Galleria
                </h3>

                <div className="space-y-4 text-sm">
                  <div className="flex items-center justify-between py-2 border-b border-white/10">
                    <span className="text-green-100 flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      Categoria
                    </span>
                    <span className="text-white font-medium capitalize bg-white/10 px-3 py-1 rounded-full">
                      {displayGallery.category}
                    </span>
                  </div>

                  <div className="flex items-center justify-between py-2 border-b border-white/10">
                    <span className="text-green-100 flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Foto
                    </span>
                    <span className="text-white font-medium">
                      {displayGallery.imageCount} immagini
                    </span>
                  </div>

                  <div className="flex items-center justify-between py-2 border-b border-white/10">
                    <span className="text-green-100 flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      Autore
                    </span>
                    <span className="text-white font-medium">
                      {displayGallery.author.profile.firstName} {displayGallery.author.profile.lastName}
                    </span>
                  </div>

                  <div className="flex items-center justify-between py-2">
                    <span className="text-green-100 flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Pubblicata
                    </span>
                    <span className="text-white font-medium">
                      {formatDate(displayGallery.createdAt)}
                    </span>
                  </div>
                </div>

                {displayGallery.tags.length > 0 && (
                  <div className="mt-5 pt-5 border-t border-white/10">
                    <span className="text-green-100 text-sm font-medium mb-3 block">Tags</span>
                    <div className="flex flex-wrap gap-2">
                      {displayGallery.tags.map((tag: string, index: number) => (
                        <span
                          key={index}
                          className="bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs font-medium border border-white/20 hover:bg-white/30 transition-colors"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Galleria Immagini */}
      <div className="container-custom py-12">
        <div
          id="gallery-detail-container"
          className={getLayoutClass(displayGallery.settings.layout)}
        >
          {displayGallery.images.map((image: GalleryImage, index: number) => (
            <a
              key={image._id}
              href={image.url}
              data-pswp-width={image.width || 1920}
              data-pswp-height={image.height || 1080}
              target="_blank"
              rel="noreferrer"
              className="cursor-pointer group block"
            >
              <div className="relative overflow-hidden rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 bg-gray-200 dark:bg-gray-800">
                <img
                  src={image.thumbnailUrl || image.url}
                  alt={image.alt || 'Immagine della galleria'}
                  className="w-full h-auto object-cover group-hover:scale-110 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center">
                  <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                    <svg className="w-12 h-12 text-white drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                    </svg>
                  </div>
                </div>
              </div>

              {image.caption && (
                <p className="mt-3 text-sm text-gray-700 dark:text-gray-300 font-medium">
                  {image.caption}
                </p>
              )}
            </a>
          ))}
        </div>

        {displayGallery.images.length === 0 && (
          <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-xl shadow-lg">
            <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-gray-500 dark:text-gray-400 text-lg font-medium">
              Questa galleria non contiene ancora immagini.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GalleryDetailPage;
